import React, { useCallback, useState } from 'react';
import { Sparkles, Loader2, Undo2 } from 'lucide-react';
import { TYPE } from '../../constants/typeScale';
import { assistWrite } from '../../services/studioService';

/**
 * THE AI ASSIST ON A FORM FIELD — one control, built to what the pattern research actually found.
 *
 * Owner 2026-08-24: *"whenever we have a form, also give the AI spark icon to help with writing; web research
 * the trend with this pattern."* The research (2026-08-24) is the reason this is not simply a sparkle button:
 *
 *  1. **A BARE SPARKLE DOES NOT MEAN AI.** Nielsen Norman Group tested the icon on 107 participants, in
 *     isolation and in context: not one said "AI". The top readings were favourite/save and visual effects,
 *     and 73% read a star as favouriting. NN/g's rule is that the icon must always carry a visible LABEL.
 *     So this control is a labelled button, never an icon alone.
 *     https://www.nngroup.com/articles/ai-sparkles-icon-problem/
 *  2. **The result is ordinary editable text, and UNDO is the escape hatch.** Atlassian's Rovo and Notion AI
 *     both write into the field and rely on the field's own editing; a bespoke accept/reject overlay that
 *     breaks native undo makes the field worse than it was. There is no modal here.
 *     https://support.atlassian.com/jira-software-cloud/docs/use-atlassian-intelligence-to-help-write-or-edit-content/
 *  3. **AI PRESENCE MUST NOT OUTLIVE HUMAN AUTHORSHIP.** IBM Carbon's AI-label spec: when a user overrides AI
 *     content the AI styling is removed and the label is replaced by a revert control. A field still badged
 *     "AI" after a human rewrote it is lying to the next reader. `aiWritten` therefore clears on the first
 *     keystroke — the consumer drops it in `onChange` — and the revert is offered only until then.
 *     https://carbondesignsystem.com/components/ai-label/usage/
 *  4. **DISCLOSURE IS MANDATORY, not polish.** Atlassian, GitLab Pajamas and AWS Cloudscape all require a
 *     "verify results" line near generated content, which is why the assisted state says so out loud.
 *  5. **The control is persistently visible while editing**, never hover-only: no verified product reveals it
 *     on hover, and hover-only would fail both discoverability and touch.
 *
 * Deliberately NOT built: a menu of rewrite actions (improve/shorten/lengthen). The research is clear that a
 * menu suits a long document with a selection in it; for a named form field with one job, one action that
 * replaces the value is the right shape, and a six-item menu on an era field would be absurd.
 */

export interface AiAssistProps {
  /** Which field this writes — the server owns the brief for each, so this is not an open prompt. */
  field: string;
  /** What is in the field now. An assist IMPROVES what is there rather than ignoring it. */
  current: string;
  /** The project this field belongs to, so the writer stays inside what the project already established. */
  seriesId?: string | null;
  /** Hand the written text back to the field. */
  onWrite: (text: string) => void;
  /** Out of credits — the caller decides what to do about it (the in-stream top-up). */
  onNeedsTopUp?: () => void;
  /** Wording for the action. Says what it DOES; "Write it for me" beats "AI". */
  label?: string;
}

const AiAssist: React.FC<AiAssistProps> = ({ field, current, seriesId, onWrite, onNeedsTopUp, label }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  /** What the field held BEFORE the assist, so the revert is exact rather than approximate. */
  const [previous, setPrevious] = useState<string | null>(null);

  const run = useCallback(async () => {
    if (busy) return;
    setBusy(true); setError('');
    const before = current;
    try {
      const r = await assistWrite(field, current, seriesId);
      if (r.needsTopUp) { onNeedsTopUp?.(); return; }
      if (!r.text) { setError('Nothing came back. Try again.'); return; }
      setPrevious(before);
      onWrite(r.text);
    } catch {
      setError('That did not come back. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }, [busy, current, field, seriesId, onWrite, onNeedsTopUp]);

  return (
    <span className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={() => void run()}
        disabled={busy}
        data-testid={`assist-${field}`}
        className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 ${TYPE.meta} font-medium text-secondary-500
                   transition-colors hover:bg-secondary-100 hover:text-secondary-800 disabled:opacity-60
                   dark:text-secondary-400 dark:hover:bg-secondary-800 dark:hover:text-secondary-100`}
      >
        {busy
          ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
          : <Sparkles className="h-3 w-3" aria-hidden />}
        {/* The LABEL is the point — the glyph alone tests as "favourite", not "AI". */}
        {busy ? 'Writing…' : (label || (current.trim() ? 'Improve this' : 'Write it for me'))}
      </button>

      {previous !== null && !busy && (
        /* Carbon's revert rule: the way back to what the human had, offered only until they edit it again. */
        <button
          type="button"
          onClick={() => { onWrite(previous); setPrevious(null); }}
          data-testid={`assist-revert-${field}`}
          className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 ${TYPE.meta} text-secondary-400
                     transition-colors hover:bg-secondary-100 hover:text-secondary-700
                     dark:hover:bg-secondary-800 dark:hover:text-secondary-200`}
        >
          <Undo2 className="h-3 w-3" aria-hidden /> Undo
        </button>
      )}

      {previous !== null && !busy && (
        <span className={`${TYPE.meta} text-secondary-400`}>Written by AI — check it.</span>
      )}
      {error && <span className={`${TYPE.meta} text-secondary-500 dark:text-secondary-400`}>{error}</span>}
    </span>
  );
};

export default AiAssist;
